import { useNavigate, useSearchParams } from '@solidjs/router';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { PageHeader } from '../components/PageHeader';
import { Button, buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { acceptInvitation, type InviteOutcome, inviteTokenFrom } from '../lib/invitations';
import { INVITE_COPY, inviteDetail, screenFor } from '../lib/invite-copy';
import { inviteResume, inviteResumeHref } from '../lib/invite-resume';
import { SESSION_TOKEN, useSessionMe } from '../lib/session';
import { absolute } from '../lib/time';

// The invitation landing — `/invite?token=…`, the URL every invitation email has
// always carried (`invitationAcceptUrl`, `packages/domain/src/entities/
// invitation.ts`). It redeems on arrival and then says exactly which of the
// API's verdicts happened. `lib/invitations.ts` owns the call and the verdict,
// `lib/invite-copy.ts` owns which sentence a verdict reads as, so this file is
// the screen and nothing else.
//
// TOP-LEVEL, never under `/org/:org`, and that is mechanism rather than filing:
// an invitee may belong to no org at all, so there is no segment to nest under
// — and off `/org/…` `apiFetch` sends no `X-Dz-Account` header, which is what
// lets the server resolve the invitation's OWN account instead of re-fencing
// the write to whichever org the URL named.
//
// A redemption that GRANTS access does not stop here. `lib/invite-resume.ts`
// says where it goes and this fires the hop: the person is mid-signup, and the
// funnel boots to the next incomplete step off the account's onboarding state.
// The copy below is what the OTHER verdicts get — the ones with nowhere to send
// anybody.

/** The server's sentence on a pending-funding accept, or `null` for every other verdict. */
function pendingDetail(outcome: InviteOutcome): string | null {
  return outcome.kind === 'pending_funding' && outcome.detail !== '' ? outcome.detail : null;
}

export function Invite(props: { session?: string | null } = {}): JSX.Element {
  // `session` is the API credential; `token` throughout this screen means the
  // INVITATION token off the query. Default to the dev/prod session only when
  // the prop is omitted — an explicit `null` is a signed-out caller.
  const session = props.session === undefined ? SESSION_TOKEN : props.session;
  const [params] = useSearchParams();
  const [outcome, setOutcome] = createSignal<InviteOutcome | null>(null);
  const navigate = useNavigate();
  // Not a second read: `<App/>`'s gate already holds this exact query key (the
  // session probe), so mounting it here joins that cache entry rather than
  // adding a request. The `refetch` below is the one call this screen actually
  // costs, and it is owed either way — the waitlist gate reads `approved` and
  // the resume reads a membership list that this redemption has just changed.
  const me = useSessionMe(session);

  const redeem = async (): Promise<void> => {
    const token = inviteTokenFrom(params['token']);
    setOutcome(null);
    const settled: InviteOutcome =
      token === null ? { kind: 'no_token' } : await acceptInvitation(token, session);
    setOutcome(settled);
    const next = inviteResume(settled);
    if (next.kind === 'stay') return;
    // AFTER the accept, never before: the membership row that turns this
    // account id into the login the app addresses an org by is the row the
    // redemption just created.
    const fresh = await me.refetch();
    // `replace`, never a push. The token is single-use, so a Back button onto
    // this URL would re-POST a spent one and answer `already_accepted` —
    // telling someone who just got in that they did not.
    navigate(inviteResumeHref(next.accountId, fresh.data?.memberships ?? []), { replace: true });
  };

  // Tracks `params['token']` and nothing else: everything the effect reads
  // synchronously is that one param, and the rest of `redeem` runs after an
  // await. So a SECOND invitation link opened into the live SPA re-redeems
  // instead of leaving the first link's verdict on screen — the tokens are
  // single-use, so one screen per token is the only correct reading.
  createEffect(() => {
    void redeem();
  });

  return (
    <section class="page">
      <PageHeader title={t('invite.title')} desc={t('invite.subtitle')} />
      <Show
        when={outcome()}
        fallback={
          <div class="alert" role="status">
            <p class="alert__title">{t('invite.checking')}</p>
          </div>
        }
      >
        {(settled) => {
          const copy = (): (typeof INVITE_COPY)[keyof typeof INVITE_COPY] =>
            INVITE_COPY[screenFor(settled())];
          const refusal = (): boolean => copy().tone === 'refusal';
          return (
            <>
              <div
                class="alert"
                classList={{ 'alert--destructive': refusal() }}
                role={refusal() ? 'alert' : 'status'}
              >
                <p class="alert__title">{t(copy().title)}</p>
                <p class="alert__desc">{t(copy().body)}</p>
                {/* The server's own sentence for an accept left unfunded, as given. */}
                <Show when={pendingDetail(settled())}>
                  {(detail) => <p class="alert__desc">{detail()}</p>}
                </Show>
                <Show when={inviteDetail(settled())}>
                  {(detail) => (
                    <p class="alert__desc">{t(detail().key, { when: absolute(detail().when) })}</p>
                  )}
                </Show>
              </div>
              {/* One next step at most, and `<div>` rather than a bare button:
                  `.page` is a flex column, so an unwrapped `.btn` would stretch
                  the width of the screen. */}
              <Show when={copy().tone === 'ok'}>
                <div>
                  {/* Bare `/`: the invitee may hold no org yet, and the
                      top-level catch-all lands a bare path in whichever one
                      they now have. */}
                  <a class={buttonClass()} href="/">
                    {t('invite.continue')}
                  </a>
                </div>
              </Show>
              {/* Recorded but not let in: `/` with a waitlisted session IS the wall,
                  which polls approval and opens the funnel once they are funded —
                  never the funnel from here. */}
              <Show when={copy().tone === 'pending'}>
                <div>
                  <a class={buttonClass()} href="/">
                    {t('invite.pending_funding_continue')}
                  </a>
                </div>
              </Show>
              <Show when={settled().kind === 'failed'}>
                <div>
                  <Button onClick={() => void redeem()}>{t('invite.retry')}</Button>
                </div>
              </Show>
            </>
          );
        }}
      </Show>
    </section>
  );
}
